/**
 * The report and the workbench's drafts of it. A picker chooses what to show:
 * the report as it stands (its latest version — the default), one of the
 * workbench's Word drafts, an earlier version, or a partner's Word return
 * (read-only, its issues kept or dismissed — `ReturnView`).
 *
 * **The report as it stands** — as the report page shows it — with approve /
 * revoke, the evidence behind its paragraphs, and its text edited in place.
 * Paragraphs carrying evidence are tinted; clicking one lists its evidence
 * (and through it, the checklist items it bears on). An item links to a
 * paragraph only when the version shown carries the paragraphs of the
 * partner's return it was found in (a byte copy, or text edits of one), so
 * the link is exact. Only one Document view is ever mounted: the anchor
 * highlights are window-wide.
 *
 * **Editing**: click a paragraph or a table cell's text and change it; the
 * edits wait (the workbench keeps them across tabs) and are saved together
 * as one new version, each carrying the text it replaces.
 *
 * **A draft** (`DraftView`): its citations, the reviewer's issues beside
 * their paragraphs, a layout check — its text edited and its layout faults
 * fixed, saved as a new draft, and Release. The draft that waits on a person
 * (built, not released, newer than the report as it stands) opens first —
 * unless a link names the document.
 *
 * **Ask the Assistant**: text selected in a document offers itself as the
 * Assistant's context — the passage and the paragraphs it touches.
 *
 * Its layouts follow the width it has (container queries), not the
 * screen's: the Assistant's column may stand beside it.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Grid,
  Group,
  Loader,
  Stack,
  Text,
} from '@mantine/core';

import * as Sentry from '@sentry/react';

import {
  DocxContent,
  editPayload,
  recheckEdits,
  type DocxEditBinding,
  type PendingEdits,
} from '@/components/Containers/DocxContent';
import { approvalLabel, ApprovalModal, ReportBody } from '@/components/Containers/ReportViewer';
import { useReportApprovals } from '@/hooks/useReportApprovals';
import { useTextSelection } from '@/hooks/useTextSelection';
import { requestAnchorHighlight, setPersistentAnchorHighlight } from '@/lib/anchorHighlight';
import { useCopilotMarks } from '@/lib/copilotHighlight';
import { ApiConflictError, vnextApiClient } from '@/services/vnext-api';
import type { CopilotPassage } from '@/types/copilot';
import {
  paragraphInDocument,
  type ChecklistItem,
  type EvidenceItem,
  type EvidencePage,
  type ReviewIssue,
  type WorkbenchDocument,
  type WorkbenchHeader,
  type WorkbenchPrefill,
} from '@/types/workbench';
import { DocumentPicker, LATEST, PREFILL } from './DocumentPicker';
import { DraftView, type DraftChanges } from './DraftView';
import { EVIDENCE_STATUS, words } from './format';
import { CONTAINER_BREAKPOINTS } from './layout';
import { PrefillView } from './PrefillView';
import { ReturnView } from './ReturnView';
import { SourceReturnsPanel } from './SourceReturnsPanel';

interface DocumentViewProps {
  header: WorkbenchHeader;
  evidence: EvidencePage | null;
  checklist: ChecklistItem[] | null;
  focusedPara: string | null;
  /** The document a link names (a draft's or a version's id). */
  focusedDoc?: string | null;
  /** A passage selected in a document, offered to the Assistant. */
  onAskCopilot?: (passage: CopilotPassage) => void;
  onOpenEvidence: (evidenceId: string) => void;
  onOpenChecklistItem: (itemId: string) => void;
  /** After an approval, a saved edit, a release or a decision (the case
   * refetches). */
  onChanged: () => void | Promise<void>;
  /** Text edits not yet saved — kept by the workbench across tabs. */
  edits: PendingEdits;
  onEditsChange: (edits: PendingEdits) => void;
  /** The report's versions and the workbench's Word drafts, newest first. */
  documents: WorkbenchDocument[] | null;
  /** The partner's form as the workbench pre-filled it: the case's own and
   * each assignment's copy. */
  prefills?: WorkbenchPrefill[] | null;
  reviewIssues: ReviewIssue[] | null;
  /** A draft's changes not yet saved — kept by the workbench across tabs. */
  draftChanges: DraftChanges | null;
  onDraftChangesChange: (changes: DraftChanges | null) => void;
  /** The review issue a link names (Needs you's, say). */
  focusedIssue?: string | null;
  /** An issue selected in a draft: the link follows. */
  onFocusIssue?: (issueId: string | null) => void;
  /** What Needs you holds, for the way back to it. */
  needsYouTotal?: number;
  /** Back to Needs you, at an item or its top. */
  onBackToNeedsYou?: (issueId: string | null) => void;
  /** Issues a person closed in a draft (Needs you remembers them). */
  onClosedHere?: (issueIds: string[]) => void;
}

type LatestVersionProps = Omit<
  DocumentViewProps,
  | 'documents'
  | 'prefills'
  | 'reviewIssues'
  | 'draftChanges'
  | 'onDraftChangesChange'
  | 'focusedDoc'
  | 'focusedIssue'
  | 'onFocusIssue'
  | 'needsYouTotal'
  | 'onBackToNeedsYou'
  | 'onClosedHere'
> & {
  /** The latest version's id (what a passage selected in it names). */
  contentId: string | null;
};

/** A selection made a passage for the Assistant: its text, kept to the
 * length a message takes. */
export function passageOf(
  document: CopilotPassage['document'],
  documentId: string,
  text: string,
  paraIds: string[]
): CopilotPassage {
  return { document, documentId, paraIds: paraIds.slice(0, 40), quote: text.slice(0, 2000) };
}

interface Notice {
  color: string;
  text: string;
}

function Approvals({
  header,
  onChanged,
}: Readonly<{ header: WorkbenchHeader; onChanged: () => void | Promise<void> }>) {
  const [open, setOpen] = useState(false);
  const { status, isMutating, approve, revoke } = useReportApprovals({
    reportId: header.reportId,
    initialStatus: header.document.approvalStatus,
  });
  if (header.document.approvalRequired === 'disabled') {
    return null;
  }
  const approved = status === 'approved';
  return (
    <Group gap="xs">
      <Badge
        size="sm"
        variant="light"
        color={approved ? 'teal' : status === 'revoked' ? 'red' : 'orange'}
        data-testid="document-approval"
      >
        {approvalLabel(status)}
      </Badge>
      <Button size="xs" variant="default" onClick={() => setOpen(true)} data-testid="approval-open">
        {approved ? 'Revoke approval…' : 'Approve…'}
      </Button>
      <ApprovalModal
        opened={open}
        onClose={() => setOpen(false)}
        approved={approved}
        mutating={isMutating}
        onConfirm={async (notes) => {
          await (approved ? revoke(notes) : approve(notes));
          await onChanged();
        }}
      />
    </Group>
  );
}

/** The draft that waits on a person: the newest, built and not yet
 * released, and newer than the report as it stands. */
function waitingDraft(
  header: WorkbenchHeader,
  documents: WorkbenchDocument[]
): WorkbenchDocument | null {
  const newest = documents.find((d) => d.kind === 'draft') ?? null;
  if (newest?.status !== 'built') {
    return null;
  }
  const latest = documents.find(
    (d) => d.kind === 'version' && d.version === header.document.latestVersion
  );
  return !latest || Date.parse(newest.createdAt) > Date.parse(latest.createdAt) ? newest : null;
}

export function DocumentView({
  documents,
  prefills = null,
  reviewIssues,
  draftChanges,
  onDraftChangesChange,
  focusedDoc = null,
  focusedIssue = null,
  onFocusIssue,
  needsYouTotal = 0,
  onBackToNeedsYou,
  onClosedHere,
  ...props
}: Readonly<DocumentViewProps>) {
  const { header, onChanged, focusedPara, onAskCopilot } = props;
  const latestVersion = header.document.latestVersion;
  const [shown, setShown] = useState<string | null>(null);
  const [released, setReleased] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const all = documents ?? [];
  const newestDraft = all.find((d) => d.kind === 'draft') ?? null;
  const latestId = all.find((d) => d.kind === 'version' && d.version === latestVersion)?.id ?? null;
  // A link naming a document opens it, over what was picked.
  useEffect(() => {
    if (focusedDoc) {
      setShown(null);
    }
  }, [focusedDoc, focusedPara]);
  const linked =
    focusedDoc && all.some((d) => d.id === focusedDoc)
      ? focusedDoc === latestId
        ? LATEST
        : focusedDoc
      : null;
  // A draft waiting on a person first; else the report as it stands, unless
  // it has no version yet: then the newest draft.
  const value =
    shown ??
    linked ??
    waitingDraft(header, all)?.id ??
    (latestVersion === null && newestDraft ? newestDraft.id : LATEST);
  const document = value === LATEST ? null : (all.find((d) => d.id === value) ?? null);
  const prefill = value.startsWith(PREFILL)
    ? ((prefills ?? []).find((p) => `${PREFILL}${p.id}` === value) ?? null)
    : null;

  // Changes made on a draft a newer one has replaced (saved elsewhere, or a
  // save here that came too late) move to the newest, which re-checks them
  // as it opens; a fix moves only where its issue came with the draft.
  useEffect(() => {
    const newest = documents?.find((d) => d.kind === 'draft');
    const on = documents?.find((d) => d.id === draftChanges?.draftId);
    if (!draftChanges || !newest || !on || newest.id === on.id) {
      return;
    }
    if (on.status === 'built' || newest.status !== 'built') {
      return;
    }
    const carried = new Set(
      (reviewIssues ?? [])
        .filter((issue) => issue.workbenchKey === newest.workbenchKey)
        .map((issue) => issue.id)
    );
    onDraftChangesChange({
      draftId: newest.id,
      edits: draftChanges.edits,
      fixes: new Set([...draftChanges.fixes].filter((id) => carried.has(id))),
    });
    setShown(newest.id);
  }, [documents, draftChanges, reviewIssues, onDraftChangesChange]);
  const chooser =
    all.length > 0 || (prefills?.length ?? 0) > 0 ? (
      <DocumentPicker
        documents={all}
        prefills={prefills ?? []}
        latestVersion={latestVersion}
        value={prefill ? `${PREFILL}${prefill.id}` : document ? document.id : LATEST}
        onChange={(id) => {
          setShown(id);
          setSaved(null);
        }}
      />
    ) : null;
  // On an evaluation copy, the live case's returns to take sit above the documents.
  const picker = header.evaluationOf ? (
    <>
      <SourceReturnsPanel
        reportId={header.reportId}
        sourceCodename={header.evaluationOf.codename}
        onTaken={onChanged}
      />
      {chooser}
    </>
  ) : (
    chooser
  );

  if (prefill) {
    return (
      <Stack gap="sm">
        {picker}
        <PrefillView header={header} prefill={prefill} />
      </Stack>
    );
  }
  if (document?.kind === 'return') {
    return (
      <Stack gap="sm">
        {picker}
        <ReturnView
          header={header}
          document={document}
          issues={(reviewIssues ?? []).filter((issue) => issue.reportDataId === document.id)}
          onChanged={onChanged}
          onAdopted={(draftId) => {
            setShown(draftId);
            setSaved(null);
          }}
        />
      </Stack>
    );
  }
  if (document) {
    return (
      <Stack gap="sm">
        {picker}
        {saved ? (
          <Alert
            color="teal"
            variant="light"
            withCloseButton
            onClose={() => setSaved(null)}
            data-testid="document-saved"
          >
            {saved}
          </Alert>
        ) : null}
        <DraftView
          header={header}
          document={document}
          issues={(reviewIssues ?? []).filter(
            (issue) => issue.workbenchKey !== null && issue.workbenchKey === document.workbenchKey
          )}
          onChanged={onChanged}
          onReleased={(notice) => {
            setReleased(notice);
            setSaved(null);
            setShown(LATEST);
          }}
          changes={draftChanges}
          onChangesChange={onDraftChangesChange}
          onSaved={(draftId, notice) => {
            setSaved(notice);
            setShown(draftId);
          }}
          focusedPara={linked === document.id ? focusedPara : null}
          onAskCopilot={onAskCopilot}
          focusedIssue={focusedIssue}
          onFocusIssue={onFocusIssue}
          needsYouTotal={needsYouTotal}
          onBackToNeedsYou={onBackToNeedsYou}
          onClosedHere={onClosedHere}
        />
      </Stack>
    );
  }
  if (value !== LATEST) {
    // A draft just saved, not in the list yet.
    return (
      <Stack gap="sm">
        {picker}
        <Center h={120}>
          <Loader size="sm" />
        </Center>
      </Stack>
    );
  }
  return (
    <Stack gap="sm">
      {picker}
      {released ? (
        <Alert
          color="teal"
          variant="light"
          withCloseButton
          onClose={() => setReleased(null)}
          data-testid="document-released"
        >
          {released}
        </Alert>
      ) : null}
      <LatestVersion {...props} contentId={latestId} />
    </Stack>
  );
}

function LatestVersion({
  header,
  evidence,
  checklist,
  focusedPara,
  onOpenEvidence,
  onOpenChecklistItem,
  onChanged,
  edits,
  onEditsChange,
  contentId,
  onAskCopilot,
}: Readonly<LatestVersionProps>) {
  const [selectedPara, setSelectedPara] = useState<string | null>(focusedPara);
  const docRef = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(edits.size > 0);
  const [active, setActive] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const items = evidence?.items ?? [];

  // Paragraph → the evidence found in it, in document order of discovery.
  const byPara = useMemo(() => {
    const map = new Map<string, EvidenceItem[]>();
    for (const item of items) {
      const paraId = paragraphInDocument(item, header.document);
      if (paraId) {
        map.set(paraId, [...(map.get(paraId) ?? []), item]);
      }
    }
    return map;
  }, [items, header.document]);
  const checklistTitles = useMemo(
    () => new Map((checklist ?? []).map((c) => [c.id, c.title])),
    [checklist]
  );

  // Tint the paragraphs with evidence while the view is open.
  const paraKey = [...byPara.keys()].join('|');
  useEffect(() => {
    setPersistentAnchorHighlight(paraKey ? paraKey.split('|') : []);
    return () => setPersistentAnchorHighlight([]);
  }, [paraKey]);

  // A paragraph in focus (a cross-link) scrolls into view and flashes.
  useEffect(() => {
    if (focusedPara) {
      setSelectedPara(focusedPara);
      requestAnchorHighlight([focusedPara]);
    }
  }, [focusedPara]);

  useTextSelection(
    docRef,
    (text, paraIds) => {
      if (contentId) {
        onAskCopilot?.(passageOf('version', contentId, text, paraIds));
      }
    },
    { enabled: Boolean(onAskCopilot && contentId) && !editing, label: 'Ask the Assistant' }
  );
  useCopilotMarks(docRef);

  if (header.document.latestVersion === null) {
    return (
      <Text size="sm" c="dimmed" data-testid="document-empty">
        No version of the report yet.
      </Text>
    );
  }

  const selected = selectedPara ? (byPara.get(selectedPara) ?? []) : [];
  const fromReturn = header.document.paragraphSourceReportDataId !== null;
  const latestVersion = header.document.latestVersion;
  const canEdit = header.document.isDocx;

  const binding: DocxEditBinding = {
    pending: edits,
    active,
    onActivate: (target) => {
      if (!saving) {
        setActive(target.key);
      }
    },
    onChange: (target, text) => {
      const next = new Map(edits);
      if (text === target.expected) {
        next.delete(target.key);
      } else {
        next.set(target.key, { target, text });
      }
      onEditsChange(next);
    },
    onClose: () => setActive(null),
    // A newer version arrived (saved elsewhere, or a save here was too
    // late): keep the edits whose paragraphs still read as they did.
    onLoad: (model) => {
      if (edits.size === 0) {
        return;
      }
      const { kept, dropped } = recheckEdits(edits, model);
      onEditsChange(kept);
      if (dropped.length) {
        setNotice({
          color: 'orange',
          text: `${dropped.length} of your edits no longer fit version ${latestVersion} and ${
            dropped.length === 1 ? 'was' : 'were'
          } dropped${kept.size ? ' — the rest are kept; check them and save again' : ''}.`,
        });
      }
    },
  };

  const save = async () => {
    const list = [...edits.values()];
    setSaving(true);
    setActive(null);
    setNotice(null);
    try {
      const saved = await vnextApiClient.saveDocumentEdits(
        header.reportId,
        latestVersion,
        editPayload(list)
      );
      onEditsChange(new Map());
      setActive(null);
      setNotice({ color: 'teal', text: `Saved as version ${saved.version}.` });
      await onChanged();
    } catch (err) {
      if (err instanceof ApiConflictError && err.detail.reason === 'stale') {
        const latest = err.detail.latest_version;
        setNotice({
          color: 'orange',
          text: `A newer version${typeof latest === 'number' ? ` (v${latest})` : ''} arrived first. Your edits now apply to it — check them and save again.`,
        });
        await onChanged();
      } else if (err instanceof ApiConflictError && err.detail.reason === 'mismatch') {
        // Failures index the edits as sent; keep the rest.
        const failures = (err.detail.failures ?? []) as { index: number; reason: string }[];
        const failed = new Set(failures.map((f) => f.index));
        const rest = new Map(
          list.filter((_, i) => !failed.has(i)).map((edit) => [edit.target.key, edit])
        );
        onEditsChange(rest);
        setNotice({
          color: 'orange',
          text: `${failed.size} edit${failed.size === 1 ? '' : 's'} couldn't be applied and ${
            failed.size === 1 ? 'was' : 'were'
          } dropped (${failures.map((f) => f.reason).join('; ')}).${
            rest.size ? ' The rest are kept — save again.' : ''
          }`,
        });
      } else {
        Sentry.captureException(err);
        setNotice({
          color: 'red',
          text: `Not saved: ${err instanceof Error ? err.message : 'something went wrong'}`,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid
      gutter="md"
      type="container"
      breakpoints={CONTAINER_BREAKPOINTS}
      data-testid="document-view"
    >
      <Grid.Col span={{ base: 12, lg: 8 }}>
        <Stack gap="sm">
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Version {latestVersion}
              {fromReturn ? " — the partner's return" : ''}
            </Text>
            <Group gap="xs">
              {canEdit ? (
                <Button
                  size="xs"
                  variant={editing ? 'filled' : 'default'}
                  onClick={() => {
                    setEditing(!editing);
                    setActive(null);
                  }}
                  data-testid="document-edit-toggle"
                >
                  {editing ? 'Stop editing' : 'Edit text'}
                </Button>
              ) : null}
              <Approvals header={header} onChanged={onChanged} />
            </Group>
          </Group>
          {notice ? (
            <Alert
              color={notice.color}
              variant="light"
              withCloseButton
              onClose={() => setNotice(null)}
            >
              {notice.text}
            </Alert>
          ) : null}
          {edits.size > 0 ? (
            <Card withBorder padding="xs" radius="sm" data-testid="document-pending">
              <Group justify="space-between">
                <Text size="sm">
                  {edits.size} change{edits.size === 1 ? '' : 's'} not saved yet
                </Text>
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    disabled={saving}
                    onClick={() => {
                      onEditsChange(new Map());
                      setActive(null);
                    }}
                  >
                    Discard
                  </Button>
                  <Button size="xs" loading={saving} onClick={() => void save()}>
                    Save as version {latestVersion + 1}
                  </Button>
                </Group>
              </Group>
            </Card>
          ) : null}
          {/* A click on a tinted paragraph picks its evidence (DocxContent
              marks blocks with data-para-id); the panel beside lists the
              same paragraphs as buttons for the keyboard. */}
          <Box
            ref={docRef}
            onClick={(e) => {
              if (editing) {
                return;
              }
              const block = (e.target as HTMLElement).closest<HTMLElement>('[data-para-id]');
              const paraId = block?.dataset.paraId;
              if (paraId && byPara.has(paraId)) {
                setSelectedPara(paraId);
              }
            }}
          >
            {/* Keyed by version: a new version (saved here or elsewhere) reloads. */}
            {editing && canEdit ? (
              <DocxContent
                key={latestVersion}
                reportId={header.reportId}
                cacheKey={`report:${header.reportId}:${latestVersion}`}
                edit={binding}
              />
            ) : (
              <ReportBody
                key={latestVersion}
                isDocx={header.document.isDocx}
                reportId={header.reportId}
                isAdmin
              />
            )}
          </Box>
        </Stack>
      </Grid.Col>
      <Grid.Col span={{ base: 12, lg: 4 }}>
        <Stack gap="sm">
          <Card withBorder padding="sm" radius="md" data-testid="document-evidence">
            <Stack gap="xs">
              <Text
                size="xs"
                fw={700}
                tt="uppercase"
                c="dimmed"
                style={{ letterSpacing: '0.06em' }}
              >
                Evidence in this version
              </Text>
              {evidence === null && <Loader size="xs" />}
              {evidence !== null && byPara.size === 0 && (
                <Text size="xs" c="dimmed">
                  {fromReturn
                    ? 'No evidence has been found in these paragraphs yet.'
                    : "This version isn't a partner's return, so evidence opens its source instead of a paragraph."}
                </Text>
              )}
              {byPara.size > 0 && selected.length === 0 && (
                <Text size="xs" c="dimmed">
                  {byPara.size} tinted paragraph{byPara.size === 1 ? '' : 's'} carry evidence —
                  click one to see it.
                </Text>
              )}
              {[...byPara].map(([paraId, paraItems]) => (
                <Anchor
                  key={paraId}
                  component="button"
                  type="button"
                  size="xs"
                  fw={paraId === selectedPara ? 700 : 400}
                  onClick={() => {
                    setSelectedPara(paraId);
                    requestAnchorHighlight([paraId]);
                  }}
                  style={{ textAlign: 'left' }}
                  data-testid={`document-para-${paraId}`}
                >
                  {paraItems[0].statement}
                </Anchor>
              ))}
              {selected.length > 0 && (
                <Stack gap={6} pt="xs" data-testid="document-selected">
                  {selected.map((item) => (
                    <Stack key={item.id} gap={2}>
                      <Group gap={6}>
                        <Badge size="xs" variant="light" color={EVIDENCE_STATUS[item.status].color}>
                          {EVIDENCE_STATUS[item.status].label}
                        </Badge>
                        <Text size="xs" c="dimmed">
                          {words(item.basis)}
                        </Text>
                      </Group>
                      <Anchor
                        component="button"
                        type="button"
                        size="xs"
                        onClick={() => onOpenEvidence(item.id)}
                        style={{ textAlign: 'left' }}
                      >
                        {item.statement}
                      </Anchor>
                      {item.links.map((link) => (
                        <Anchor
                          key={link.checklistItemId}
                          component="button"
                          type="button"
                          size="xs"
                          c="dimmed"
                          onClick={() => onOpenChecklistItem(link.checklistItemId)}
                          style={{ textAlign: 'left' }}
                        >
                          {words(link.relation)}{' '}
                          {checklistTitles.get(link.checklistItemId) ?? 'a checklist item'}
                        </Anchor>
                      ))}
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>
          </Card>
        </Stack>
      </Grid.Col>
    </Grid>
  );
}
